import React from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"

export interface ConfirmDialogProps {
  /** Controlled open state. Omit to use uncontrolled mode with `trigger`. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Element that opens the dialog (uncontrolled mode). */
  trigger?: React.ReactNode
  title: string
  description: string | React.ReactNode
  confirmText?: string
  cancelText?: string
  variant?: "default" | "destructive"
  onConfirm: () => void | Promise<void>
  isLoading?: boolean
  loadingText?: string
  children?: React.ReactNode
}

export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  onConfirm,
  isLoading = false,
  loadingText = "Processing...",
  children
}: ConfirmDialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const isControlled = open !== undefined
  const dialogOpen = isControlled ? open : internalOpen
  const setDialogOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen

  const handleConfirm = async () => {
    try {
      await onConfirm()
      if (!isControlled) setInternalOpen(false)
    } catch (error) {
      console.error("Error in confirm action:", error)
    }
  }

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {children && (
          <div className="py-4">
            {children}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={variant === "destructive" ? "bg-red-600 hover:bg-red-700" : ""}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {loadingText}
              </>
            ) : (
              confirmText
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Specialized delete confirmation dialog
export interface DeleteConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  itemName?: string
  itemType?: string
  description?: string | React.ReactNode
  onConfirm: () => void | Promise<void>
  isLoading?: boolean
  children?: React.ReactNode
}

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  title,
  itemName,
  itemType = "item",
  description,
  onConfirm,
  isLoading = false,
  children
}: DeleteConfirmDialogProps) {
  const defaultTitle = `Delete ${itemType}`
  const defaultDescription = itemName
    ? `Are you sure you want to delete "${itemName}"? This action cannot be undone. This will permanently delete the ${itemType}.`
    : `Are you sure you want to delete this ${itemType}? This action cannot be undone. This will permanently delete the ${itemType}.`

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title || defaultTitle}
      description={description || defaultDescription}
      confirmText="Delete"
      cancelText="Cancel"
      variant="destructive"
      onConfirm={onConfirm}
      isLoading={isLoading}
      loadingText="Deleting..."
    >
      {children}
    </ConfirmDialog>
  )
}

// Specialized action confirmation dialog
export interface ActionConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string | React.ReactNode
  actionText: string
  cancelText?: string
  variant?: "default" | "destructive"
  onConfirm: () => void | Promise<void>
  isLoading?: boolean
  loadingText?: string
  children?: React.ReactNode
}

export function ActionConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  actionText,
  cancelText = "Cancel",
  variant = "default",
  onConfirm,
  isLoading = false,
  loadingText = "Processing...",
  children
}: ActionConfirmDialogProps) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      confirmText={actionText}
      cancelText={cancelText}
      variant={variant}
      onConfirm={onConfirm}
      isLoading={isLoading}
      loadingText={loadingText}
    >
      {children}
    </ConfirmDialog>
  )
}
